import axios from "axios";
import { synchronizedTokenRefresh } from "../utils/security";
import { resolveApiBaseUrl } from "../utils/apiBase";

//! #46: MUST resolve to the same origin as services/api.js — the refresh cookie is
//! set by /auth/login and read by /auth/refresh, so if these two clients disagreed on
//! the origin the cookie would be written to one and never sent to the other. On
//! localhost this is the relative "/api/v1" (see src/setupProxy.js); elsewhere it is
//! REACT_APP_AUTH_URL exactly as before.
const AUTH_URL = resolveApiBaseUrl(process.env.REACT_APP_AUTH_URL);

const api = axios.create({
  baseURL: AUTH_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Important for sending/receiving cookies
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only try to refresh if:
    // 1. It's a 401 or 403 error
    // 2. We haven't tried to refresh already
    // 3. It's not the refresh endpoint itself failing
    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !originalRequest._retry &&
      !originalRequest.url.includes("/auth/refresh")
    ) {
      originalRequest._retry = true;
      try {
        console.log("Token expired or invalid. Attempting to refresh...");
        
        const access_token = await synchronizedTokenRefresh(async () => {
            const response = await api.post("/auth/refresh");
            const { access_token } = response.data;
            localStorage.setItem("accessToken", access_token);
            return access_token;
        });

        // Update the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (err) {
        // Refresh failed - clear everything and redirect to login
        console.error("Token refresh failed:", err);
        localStorage.removeItem("accessToken");
        window.location.href = "/login";
        return Promise.reject(err);
      }
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  register: async (email, password) => {
    try {
      // Get browser info for device_info object
      const userAgent = navigator.userAgent;
      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;

      const response = await api.post(
        "/auth/register",
        {
          user_in: { email, password },
          device_info: {
            user_agent: userAgent,
            screen_width: screenWidth,
            screen_height: screenHeight,
            platform: navigator.platform,
          },
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const { access_token, user } = response.data;
      localStorage.setItem("accessToken", access_token);
      return { user, access_token };
    } catch (error) {
      console.error("Registration error details:", error.response);
      handleAuthError(error);
    }
  },

  login: async (email, password) => {
    try {
      // Get browser info for device_info object
      const userAgent = navigator.userAgent;
      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;

      // Get OS platform information
      const platform = navigator.platform;
      const oscpu = navigator.oscpu || "";
      const isAndroid = /android/i.test(userAgent);
      const isIOS = /iPad|iPhone|iPod/.test(userAgent);

      // Construct device_info object with device details
      const device_info = {
        user_agent: userAgent,
        screen_width: screenWidth,
        screen_height: screenHeight,
        platform: platform,
        os_details: oscpu,
        is_mobile: isAndroid || isIOS,
        app_version: "1.0",
      };

      console.log("Login attempt with email:", email);

      // Create URLSearchParams for the form data part
      const formData = new URLSearchParams();
      formData.append("username", email); // OAuth2 uses 'username' field even for email
      formData.append("password", password);

      // For FastAPI's handling of form + json body, we need to create a multipart request
      // or use a library that supports sending both form data and JSON in one request

      // We'll use axios with URLSearchParams for form data, and pass device_info as a custom header
      // This approach avoids using multipart/form-data which would require additional handling
      const response = await api.post("/auth/login", formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-Device-Info": JSON.stringify(device_info), // Pass device info as a header
        },
      });

      console.log("Login successful, processing response");
      const { access_token, user } = response.data;
      localStorage.setItem("accessToken", access_token);
      return { user, access_token };
    } catch (error) {
      console.error("Login error details:", error.response);
      // Additional debug info
      if (error.response && error.response.data) {
        console.error(
          "Error response data:",
          JSON.stringify(error.response.data)
        );
      }
      handleAuthError(error);
    }
  },

  logout: async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      localStorage.removeItem("accessToken");
    }
  },

  getCurrentUser: async () => {
    try {
      // Get user data from auth service gateway
      const response = await api.get("/auth/me");

      // Log the response for debugging
      console.log(
        "API getCurrentUser response from auth service:",
        response.data
      );

      // Normalize the data structure to ensure consistent access patterns
      const userData = response.data;

      // Extract profile fields from profile_data if they exist but aren't at the top level
      if (userData.profile_data) {
        // If there's profile_data but phone is missing at top level, use profile_data.phone
        if (!userData.phone && userData.profile_data.phone) {
          userData.phone = userData.profile_data.phone;
        }

        // Same for phoneNumber (allowing for different naming conventions)
        if (!userData.phone && userData.profile_data.phoneNumber) {
          userData.phone = userData.profile_data.phoneNumber;
        }

        // If there's profile_data but address is missing at top level, use profile_data.address
        if (!userData.address && userData.profile_data.address) {
          userData.address = userData.profile_data.address;
        }

        // Handle any other fields that might be nested in profile_data
        if (!userData.bio && userData.profile_data.bio) {
          userData.bio = userData.profile_data.bio;
        }

        if (!userData.displayName && userData.profile_data.displayName) {
          userData.displayName = userData.profile_data.displayName;
        }

        // Lift subscription_plan if it's in profile_data but missing at top level
        if (!userData.subscription_plan && userData.profile_data.subscription_plan) {
          userData.subscription_plan = userData.profile_data.subscription_plan;
        }
      }

      console.log("Normalized user data:", userData);

      return userData;
    } catch (error) {
      console.error("Error fetching current user:", error);

      // Check if it's an authentication issue
      if (
        error.response &&
        (error.response.status === 401 || error.response.status === 403)
      ) {
        // Try to refresh token if not already trying
        if (!error.config._retry) {
          try {
            await authApi.refreshToken();
            // Retry getting the user data
            const retryResponse = await api.get("/auth/me");

            // Apply the same normalization to the retry response
            const userData = retryResponse.data;
            if (userData.profile_data) {
              if (!userData.phone && userData.profile_data.phone) {
                userData.phone = userData.profile_data.phone;
              }
              if (!userData.phone && userData.profile_data.phoneNumber) {
                userData.phone = userData.profile_data.phoneNumber;
              }
              if (!userData.address && userData.profile_data.address) {
                userData.address = userData.profile_data.address;
              }
              if (!userData.bio && userData.profile_data.bio) {
                userData.bio = userData.profile_data.bio;
              }
              if (!userData.displayName && userData.profile_data.displayName) {
                userData.displayName = userData.profile_data.displayName;
              }
              
              // Lift subscription_plan
              if (!userData.subscription_plan && userData.profile_data.subscription_plan) {
                userData.subscription_plan = userData.profile_data.subscription_plan;
              }
            }

            console.log("Normalized user data after token refresh:", userData);
            return userData;
          } catch (refreshError) {
            console.error("Token refresh failed:", refreshError);
            // Clear token and redirect to login
            localStorage.removeItem("accessToken");
            window.location.href = "/login";
          }
        }
      }
      throw error;
    }
  },

  refreshToken: async () => {
    try {
      const access_token = await synchronizedTokenRefresh(async () => {
          const response = await api.post("/auth/refresh");
          const { access_token } = response.data;
          localStorage.setItem("accessToken", access_token);
          return access_token;
      });
      return { access_token };
    } catch (error) {
      console.error("Token refresh error:", error);
      //! Only destroy the token when the server actually REJECTED us. This used to
      //! remove it on ANY error, including a network blip / unreachable backend —
      //! which contradicts the deliberate policy in AuthContext's init path ("For
      //! network errors we DO NOT clear the session") and silently nuked a still-valid
      //! session on a transient failure. A rejected refresh is unrecoverable; an
      //! unreachable backend is not, and the next attempt may well succeed.
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem("accessToken");
      }
      throw error;
    }
  },

  updateProfile: async (profileData) => {
    try {
      console.log("Original profile data to be sent:", profileData);

      // Format data to ensure phones and addresses are properly named
      const formattedData = {
        ...profileData,
        // Ensure we have phone and address fields even if they're in profile_data
        phone:
          profileData.phoneNumber ||
          profileData.phone ||
          (profileData.profile_data && profileData.profile_data.phoneNumber),
        address:
          profileData.address ||
          (profileData.profile_data && profileData.profile_data.address),
      };

      console.log(
        "Formatted profile data to send to auth service:",
        formattedData
      );

      // Update through auth service only - it will handle the user-management service internally
      const response = await api.post("/auth/profile", formattedData);

      // Store the new access token
      const { access_token, user } = response.data;
      localStorage.setItem("accessToken", access_token);

      return { user, access_token };
    } catch (error) {
      console.error("Profile update error:", error.response);
      handleAuthError(error);
    }
  },
};

// Helper function to handle auth-related errors
const handleAuthError = (error) => {
  if (!error.response) {
    throw new Error("Network error. Please check your internet connection.");
  }

  const status = error.response.status;
  const detail = error.response.data?.detail;

  switch (status) {
    case 400:
      throw new Error(detail || "Incorrect email or password");
    case 401:
      throw new Error(detail || "Authentication failed");
    case 403:
      throw new Error(detail || "You don't have permission");
    case 409:
      throw new Error(detail || "Email already exists");
    case 422:
      if (Array.isArray(error.response.data.detail)) {
        const validationErrors = error.response.data.detail
          .map((err) => {
            const field = err.loc?.slice(-1)[0] || "field";
            return `${field}: ${err.msg}`;
          })
          .join(", ");
        throw new Error(validationErrors || "Invalid input format");
      }
      throw new Error(detail || "Invalid input format");
    case 500:
      throw new Error("Server error. Please try again later.");
    default:
      throw new Error(detail || "An unexpected error occurred");
  }
};

export default api;
